import { currencyInfo, isSupportedCurrency } from './currency.ts';
import type { Money } from './money.ts';

export type FormatMoneyOptions = {
  /** 'auto' (default): minus only; 'exceptZero': +/− for non-zero; 'always'; 'never'. */
  readonly signDisplay?: 'auto' | 'always' | 'exceptZero' | 'never';
  readonly currencyDisplay?: 'symbol' | 'narrowSymbol' | 'code' | 'name';
  /** Hide the minor part when it is zero (e.g. axis labels). Default false. */
  readonly trimZeroMinor?: boolean;
};

const formatterCache = new Map<string, Intl.NumberFormat>();

function formatter(
  locale: string,
  currency: string,
  fractionDigits: number,
  options: FormatMoneyOptions,
): Intl.NumberFormat {
  // Polish CLDR data prints "GBP"/"USD" with the regular symbol; the narrow one gives "£"/"$",
  // which is how Polish-speaking users in the UK write it. English keeps "PLN 4,851.24".
  const display =
    options.currencyDisplay ?? (locale.toLowerCase().startsWith('pl') ? 'narrowSymbol' : 'symbol');
  const key = [locale, currency, fractionDigits, options.signDisplay ?? 'auto', display].join('|');
  const cached = formatterCache.get(key);
  if (cached) return cached;
  const base: Intl.NumberFormatOptions = {
    style: 'currency',
    currency,
    currencyDisplay: display,
    signDisplay: options.signDisplay ?? 'auto',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  };
  let created: Intl.NumberFormat;
  try {
    // Polish banking style groups 4-digit numbers too ("4 851,24 zł"); CLDR's default does not.
    created = new Intl.NumberFormat(locale, { ...base, useGrouping: 'always' });
  } catch {
    // Engines without Intl.NumberFormat v3 (older Hermes) reject string useGrouping.
    created = new Intl.NumberFormat(locale, { ...base, useGrouping: true });
  }
  formatterCache.set(key, created);
  return created;
}

/** A piece of a formatted amount: the sign, the number, the currency's sign and the space between. */
export type MoneyPart = { readonly type: 'sign' | 'number' | 'currency' | 'literal'; readonly value: string };

type Placement = { readonly symbol: string; readonly before: boolean; readonly space: boolean };
const placementCache = new Map<string, Placement | null>();

/**
 * The currency's sign as its own country writes it: the symbol (English CLDR's narrow one — "£",
 * "zł", "$", "¥"), and whether it stands before the number and with a space, from the home locale.
 */
function placement(currency: string): Placement | null {
  if (placementCache.has(currency)) return placementCache.get(currency)!;
  let found: Placement | null = null;
  try {
    const home = isSupportedCurrency(currency) ? currencyInfo(currency).home : undefined;
    if (home) {
      const parts = new Intl.NumberFormat(home, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).formatToParts(1234.5);
      const at = parts.findIndex((p) => p.type === 'currency');
      const number = parts.findIndex((p) => p.type === 'integer');
      const symbol = new Intl.NumberFormat('en', { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).formatToParts(1).find((p) => p.type === 'currency')?.value;
      if (at >= 0 && number >= 0 && symbol) {
        const before = at < number;
        const neighbour = parts[before ? at + 1 : at - 1];
        found = { symbol, before, space: neighbour?.type === 'literal' && /\s/.test(neighbour.value) };
      }
    }
  } catch {
    found = null;
  }
  placementCache.set(currency, found);
  return found;
}

/**
 * Locale-aware, exact money formatting. The digits come from bigint arithmetic, so large
 * amounts are never rounded by floating point. The number is written as in the UI language's
 * country; the currency's sign as in the currency's own (the owner, 30.09.2026):
 *   pl-PL: "4 851,24 zł", "£4 851,24"   en-GB: "£4,851.24", "4,851.24 zł"
 * An explicit `currencyDisplay`, and a currency without a home (the euro), follow the UI locale.
 */
export function formatMoney(value: Money, locale: string, options: FormatMoneyOptions = {}): string {
  return formatMoneyParts(value, locale, options)
    .map((part) => part.value)
    .join('');
}

/** `formatMoney` in pieces, so a view can style or drop the currency's sign. */
export function formatMoneyParts(value: Money, locale: string, options: FormatMoneyOptions = {}): MoneyPart[] {
  const scale = currencyInfo(value.currency).scale;
  const factor = 10n ** BigInt(scale);
  const negative = value.minor < 0n;
  const absMinor = negative ? -value.minor : value.minor;
  const whole = absMinor / factor;
  const fraction = absMinor % factor;
  const trim = options.trimZeroMinor === true && fraction === 0n;
  const digits = trim ? 0 : scale;
  const nf = formatter(locale, value.currency, digits, options);

  // Template value with the same sign and integer part; a tiny fraction keeps "exceptZero"
  // and negative-zero signs right for amounts below one major unit.
  let template: number | bigint;
  if (whole <= BigInt(Number.MAX_SAFE_INTEGER)) {
    template = Number(whole) + (whole === 0n && fraction > 0n ? 0.1 : 0);
    if (negative) template = -template;
  } else {
    template = negative ? -whole : whole;
  }

  const fractionText = digits > 0 ? fraction.toString().padStart(scale, '0') : '';
  if (typeof nf.formatToParts !== 'function') {
    return [{ type: 'number', value: nf.format(template as number) }];
  }
  const parts = nf.formatToParts(template as number).map((part) => (part.type === 'fraction' ? { ...part, value: fractionText } : part));
  return arrange(parts, value.currency, options.currencyDisplay === undefined);
}

const NO_BREAK_SPACE = String.fromCharCode(0xa0);

/**
 * Intl's pieces of an amount in the UI locale, with the currency's sign moved to where its own
 * country puts it (a no-break space when it keeps one there, so the pair never splits).
 */
function arrange(parts: Intl.NumberFormatPart[], currency: string, own: boolean): MoneyPart[] {
  const kind = (type: Intl.NumberFormatPartTypes): MoneyPart['type'] =>
    type === 'currency' ? 'currency' : type === 'minusSign' || type === 'plusSign' ? 'sign' : type === 'literal' ? 'literal' : 'number';
  const place = own ? placement(currency) : null;
  if (!place) return parts.map((part) => ({ type: kind(part.type), value: part.value }));
  const sign = parts.filter((p) => p.type === 'minusSign' || p.type === 'plusSign').map((p) => p.value).join('');
  const number = parts.filter((p) => ['integer', 'group', 'decimal', 'fraction'].includes(p.type)).map((p) => p.value).join('');
  const out: MoneyPart[] = [];
  if (sign) out.push({ type: 'sign', value: sign });
  const symbol: MoneyPart = { type: 'currency', value: place.symbol };
  const gap: MoneyPart[] = place.space ? [{ type: 'literal', value: NO_BREAK_SPACE }] : [];
  if (place.before) out.push(symbol, ...gap, { type: 'number', value: number });
  else out.push({ type: 'number', value: number }, ...gap, symbol);
  return out;
}

/**
 * A price with more decimals than the currency's minor unit (a share's price, up to four), laid
 * out like money. Any currency code Intl knows, also one this package does not keep (GBX).
 */
export function formatPrice(amount: number, currency: string, locale: string, maximumFractionDigits = 4): string {
  const nf = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: locale.toLowerCase().startsWith('pl') ? 'narrowSymbol' : 'symbol',
    maximumFractionDigits,
  });
  return arrange(nf.formatToParts(amount), currency, true)
    .map((part) => part.value)
    .join('');
}
